/**
 * Hook that injects mock data into the Zustand store for the guided tour.
 * inject() is idempotent — safe to call multiple times (subsequent calls are no-ops).
 * cleanup() deselects both mock flights and resets the injection flag.
 *
 * Uses useAppStore.getState() inside callbacks so they never become stale and
 * do not subscribe the component to the entire store.
 *
 * injectFlightData() is exported as a standalone function so selectFlight()
 * can call it in demo mode to restore mock data after a re-selection.
 */

import { useCallback, useRef } from 'react';
import { useAppStore } from '../store/appStore';
import {
  MOCK_FLIGHT_ACTIVE,
  MOCK_FLIGHT_ENDED,
  MOCK_MESSAGES_ACTIVE,
  MOCK_MESSAGES_ENDED,
  MOCK_PREDICTION_INSTANT,
  MOCK_PREDICTION_NORMAL,
  MOCK_SEPARATORS,
  MOCK_TEAMS,
} from './mockTourData';

/**
 * Re-injects mock data for a single flight into the store.
 * Called by selectFlight() when isTourDemo is true so that re-selecting a mock
 * flight restores its telemetry, teams, and predictions without a polling cycle.
 *
 * Uses useAppStore.setState (direct overwrite) for messages to stay idempotent —
 * setMessages() merges arrays and would duplicate if Correctif A already preserved
 * the data. setTeams/setPrediction are replacements, so they are safe either way.
 *
 * @param flightId - ID of the mock flight whose data must be restored.
 */
export function injectFlightData(flightId: number): void {
  const s = useAppStore.getState();

  if (flightId === MOCK_FLIGHT_ACTIVE.id) {
    useAppStore.setState((state) => ({
      messages: { ...state.messages, [flightId]: MOCK_MESSAGES_ACTIVE },
    }));
    s.setTeams(flightId, MOCK_TEAMS);
  } else if (flightId === MOCK_FLIGHT_ENDED.id) {
    useAppStore.setState((state) => ({
      messages: { ...state.messages, [flightId]: MOCK_MESSAGES_ENDED },
    }));
    s.setPrediction(flightId, 'normal', MOCK_PREDICTION_NORMAL);
    s.setPrediction(flightId, 'instant', MOCK_PREDICTION_INSTANT);
    s.setFlightPref(flightId, 'showNormal', true);
    s.setFlightPref(flightId, 'showInstant', true);
  }
}

export interface UseTourMockResult {
  /** Inject mock data into the store. Safe to call multiple times. */
  inject: () => void;
  /** Remove mock data from the store and reset injection state. */
  cleanup: () => void;
}

/**
 * Provides inject/cleanup helpers that load and unload mock tour data.
 * Both functions are stable across renders (useCallback with empty deps).
 */
export function useTourMock(): UseTourMockResult {
  const injectedRef = useRef(false);

  const inject = useCallback(() => {
    if (injectedRef.current) return;
    injectedRef.current = true;

    const s = useAppStore.getState();

    // 0. Deselect any real flights from a previous session cookie
    for (const id of [...s.selectedFlightIds]) {
      s.deselectFlight(id);
    }

    // 1. Set mock separators (for the flight selection panel)
    s.setSeparators(MOCK_SEPARATORS);

    // 2. Select both flights — selectFlight's Correctif B guard calls
    //    injectFlightData() for each, which populates messages/teams/predictions.
    //    No separate setMessages/setTeams/setPrediction calls needed here;
    //    duplicating them would double-merge messages (setMessages accumulates).
    s.selectFlight(MOCK_FLIGHT_ACTIVE.id, MOCK_FLIGHT_ACTIVE);
    s.selectFlight(MOCK_FLIGHT_ENDED.id, MOCK_FLIGHT_ENDED);

    // 3. Open panel, active tab on the ended flight (richer data set)
    s.setPanelOpen(true);
    s.setActivePanelFlight(MOCK_FLIGHT_ENDED.id);
  }, []);

  const cleanup = useCallback(() => {
    if (!injectedRef.current) return;
    injectedRef.current = false;

    const s = useAppStore.getState();
    s.deselectFlight(MOCK_FLIGHT_ACTIVE.id);
    s.deselectFlight(MOCK_FLIGHT_ENDED.id);
    s.setSeparators([]);
  }, []);

  return { inject, cleanup };
}
